/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { formatCurrency } from '../../utils/formatters.ts';
import { renderStatusBadge } from '../../utils/renderings.tsx';
import {
  calculateGrossTotal,
  calculateNetPayment,
  calculateNetTotal,
  calculateMerchSherpaFee,
  getOrderPrintfulCosts
} from '../../utils/calculations.ts';

interface TagSummaryProps {
  selectedTag: any;
  isAdmin: boolean;
  calculateOrderTotalEarned: (order: any) => number;
}

const TagSummary: React.FC<TagSummaryProps> = ({
  selectedTag,
  isAdmin,
  calculateOrderTotalEarned,
}) => {
  if (!selectedTag) return null;

  const totalMerchSherpaFees = selectedTag.orders?.reduce((total: number, order: any) => {
  const orderTotals = getOrderPrintfulCosts(order);
  const grossTotal = calculateGrossTotal(order.lineItems);
  const shopifyPaymentFee = parseFloat(order.shopifyPaymentFee?.toString()) || 0;
  const tip = parseFloat(order.shopifyTip?.toString()) || 0;
  const printfulDiscount = parseFloat(order.printfulDiscount?.toString()) || 0;
  
  const calculatedFulfillmentCost = orderTotals.subtotal + orderTotals.digitization + 
                                     orderTotals.shipping + orderTotals.retailDeliveryFee + 
                                     orderTotals.tax;
  
  const grossTotalWithTip = grossTotal + tip;
  const netPayment = calculateNetPayment(grossTotalWithTip, shopifyPaymentFee);
  const netTotal = calculateNetTotal(netPayment, calculatedFulfillmentCost);
  
  // Include tip when calculating fees (matching sales grid logic)
  const adjustedNetTotal = netTotal + printfulDiscount;
  const merchSherpaFee = calculateMerchSherpaFee(adjustedNetTotal);
  
  return Math.round((total + merchSherpaFee) * 100) / 100; // Round to 2 decimals to prevent precision errors
}, 0) || 0;

  const totalEarnedAllOrders = selectedTag.orders?.reduce((total: number, order: any) => {
    const earned = calculateOrderTotalEarned(order);
    return Math.round((total + earned) * 100) / 100; // Round to 2 decimals to prevent precision errors
  }, 0) || 0;

  return (
    <div className="tag-summary">
      <h3 className="tag-summary-title">Totals</h3>
      <div className="tag-summary-grid">
        <div className="summary-item">
          <strong>Order Count:</strong>
          <span>{selectedTag.orderCount || 0}</span>
        </div>
        <div className="summary-item">
          <strong>Item Count:</strong>
          <span>{selectedTag.itemCount || 0}</span>
        </div>
        
        {selectedTag.totalTips > 0 && (
          <div id="total-tips-received" className="summary-item">
            <strong>Tips Received:</strong>
            <span className="tip-amount">+{formatCurrency(selectedTag.totalTips)}</span>
          </div>
        )}

        {selectedTag.totalPrintfulDiscounts > 0 && (
          <div className="summary-item" style={{ 
            backgroundColor: '#d1ecf1', 
            padding: '4px', 
            borderRadius: '4px',
            border: '1px solid #bee5eb'
          }}>
            <strong>Sherpa Network Discount:</strong>
            <span style={{color: '#0c5460', fontWeight: 'bold'}}>
              +{formatCurrency(selectedTag.totalPrintfulDiscounts)}
            </span>
          </div>
        )}
        
        {isAdmin && (
          <div className="summary-item" style={{ 
            backgroundColor: '#fff3cd', 
            padding: '4px', 
            borderRadius: '4px',
            border: '1px solid #ffeaa7'
          }}>
            <strong>Total Merch Sherpa Profit:</strong>
            <span style={{color: '#856404', fontWeight: 'bold'}}>
              {formatCurrency(totalMerchSherpaFees)}
            </span>
          </div>
        )}

        <div className="summary-item total-earned-summary">
          <strong>Total Earned:</strong>
          <span className="earned-amount bolded">
            {formatCurrency(totalEarnedAllOrders)}
          </span>
        </div>

        {selectedTag.totalPrintfulDigitizationFees > 0 && (
          <div className="summary-item digitization-summary" style={{ 
            backgroundColor: '#d4edda', 
            padding: '4px', 
            borderRadius: '4px' 
          }}>
            <strong>Digitization Costs:</strong>
            <span className="cost-amount">
              -{formatCurrency(selectedTag.totalPrintfulDigitizationFees)}
            </span>
            <span style={{ fontSize: '10px', color: '#666', marginLeft: '5px' }}>
              (Order-level)
            </span>
          </div>
        )}
      </div>
      
      {selectedTag.statusCounts && (
        <div className="status-summary">
          <h4 className="status-summary-title">Status Summary:</h4>
          <div className="status-summary-grid">
            <div className="status-summary-section">
              <strong>Financial Status:</strong>
              <div className="status-badges">
                {Object.entries(selectedTag.statusCounts.financial || {}).map(([status, count]) => 
                  count > 0 ? (
                    <div key={status} className="status-count">
                      {renderStatusBadge(status, 'financial')} <span className="count">{count as number}</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>
            <div className="status-summary-section">
              <strong>Fulfillment Status:</strong>
              <div className="status-badges">
                {Object.entries(selectedTag.statusCounts.fulfillment || {}).map(([status, count]) => 
                  count > 0 ? (
                    <div key={status} className="status-count">
                      {renderStatusBadge(status, 'fulfillment')} <span className="count">{count as number}</span>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSummary;